import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  Analytics,
  ArrowForward,
  Assessment,
  Business,
  CheckCircle,
  CloudSync,
  DeviceHub,
  HomeOutlined,
  Map,
  NotificationsActive,
  Payments,
  Sensors,
  Shield,
  Speed,
  WaterDrop,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

import Navbar from './Navbar';
import Footer from './Footer';
import Custom from './Assets/custom.webp';
import Table from './Assets/table.webp';
import meter1 from './Assets/meter1.webp';
import dashboard from './Assets/smart.jpeg';
import dom1 from './Assets/dom1.webp';
import dash1 from './Assets/dash1.webp';
import bulk3 from './Assets/bulk3.webp';
import data from './Assets/data.webp';
import conserve from './Assets/conserve.webp';
import Installation1 from './Assets/Installation1.webp';
import smartmetering from './Assets/smartmetering.webp';
import Installation3 from './Assets/Installation3.webp';
import wra from './Assets/wra.webp';
import gatanga from './Assets/gatanga.jpg';
import ruiru from './Assets/ruiru.jpg';
import bamburi from './Assets/bamburi.jpg';
import aquamist from './Assets/aquamist.jpg'
import farmers from './Assets/farmers.jpg';
import domestic from './Assets/domestic.jpeg';


const MotionDiv = motion.div;

const Main = styled.div`
  min-height: 100vh;
  background: #f6f9fc;
  color: #0f172a;
`;

const Container = styled.main`
  padding-top: 76px;
`;

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(circle at 82% 18%, rgba(14, 165, 233, 0.34), transparent 34%),
    linear-gradient(135deg, #06111f 0%, #0b1f3a 45%, #075985 100%);
  color: white;
`;

const HeroInner = styled.div`
  width: min(1180px, calc(100% - 36px));
  min-height: calc(100vh - 76px);
  margin: 0 auto;
  padding: 86px 0 54px;
  display: grid;
  grid-template-columns: minmax(0, 1.02fr) minmax(360px, 0.98fr);
  align-items: center;
  gap: clamp(36px, 6vw, 76px);

  @media (max-width: 940px) {
    min-height: auto;
    grid-template-columns: 1fr;
    padding: 64px 0 42px;
  }
`;

const Eyebrow = styled.div`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid rgba(186, 230, 253, 0.38);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #bae6fd;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  max-width: 780px;
  margin: 20px 0 18px;
  font-size: clamp(2.8rem, 6vw, 5.5rem);
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
`;

const HeroText = styled.p`
  max-width: 660px;
  margin: 0;
  color: #dbeafe;
  font-size: clamp(1.02rem, 1.8vw, 1.22rem);
  line-height: 1.8;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 32px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 48px;
  padding: 0 22px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: #2563eb;
  color: white;
  font-size: 0.96rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease;
  box-shadow: 0 18px 35px rgba(37, 99, 235, 0.28);

  &:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 22px 45px rgba(37, 99, 235, 0.34);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.22);
  color: white;
  box-shadow: none;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.36);
    box-shadow: none;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 44px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);

  strong {
    display: block;
    color: white;
    font-size: 1.45rem;
    line-height: 1;
  }

  span {
    display: block;
    margin-top: 8px;
    color: #cbd5e1;
    font-size: 0.9rem;
    line-height: 1.45;
  }
`;

const HeroVisual = styled.div`
  position: relative;
  min-height: 520px;

  @media (max-width: 940px) {
    min-height: 460px;
  }

  @media (max-width: 560px) {
    min-height: 410px;
  }
`;

const DeviceFrame = styled.div`
  position: absolute;
  inset: 36px 0 auto auto;
  width: min(92%, 520px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.34);


  img {
    display: block;
    width: 100%;
    height: 330px;
    object-fit: cover;
    
  }

  @media (max-width: 940px) {
    left: 0;
    right: auto;
    width: min(100%, 620px);
  }

  @media (max-width: 560px) {
    inset: 18px 0 auto 0;

    img {
      height: 250px;
    }
  }
`;

const MeterCard = styled.div`
  position: absolute;
  left: 0;
  bottom: 34px;
  width: min(54%, 286px);
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 25px 65px rgba(2, 6, 23, 0.28);

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: contain;
  }

  @media (max-width: 560px) {
    width: 52%;
    bottom: 22px;
    padding: 12px;
  }
`;

const SignalCard = styled.div`
  position: absolute;
  right: 14px;
  bottom: 18px;
  width: min(52%, 270px);
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 22px;
  background: rgba(6, 17, 31, 0.82);
  backdrop-filter: blur(16px);
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.24);

  h3 {
    margin: 0 0 14px;
    color: white;
    font-size: 0.98rem;
  }

  p {
    margin: 0;
    color: #bae6fd;
    font-size: 0.86rem;
    line-height: 1.55;
  }

  @media (max-width: 560px) {
    right: 0;
    bottom: 18px;
    width: 58%;
    padding: 14px;
  }
`;

const PulseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
`;

const PulseTrack = styled.div`
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);

  span {
    display: block;
    width: 76%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #22d3ee, #60a5fa);
  }
`;

const Section = styled(motion.section)`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 86px 0;

  @media (max-width: 720px) {
    padding: 62px 0;
  }
`;

const SectionHeader = styled.div`
  max-width: 760px;
  margin-bottom: 34px;

  ${({ centered }) => centered && `
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  `}
`;

const Kicker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 12px 0 0;
  color: #0f172a;
  font-size: clamp(2rem, 3.6vw, 3.3rem);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: 0;
`;

const Text = styled.p`
  margin: 16px 0 0;
  color: #475569;
  font-size: 1.02rem;
  line-height: 1.8;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 940px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article`
  min-height: 228px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: white;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.055);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(37, 99, 235, 0.32);
    box-shadow: 0 24px 55px rgba(37, 99, 235, 0.11);
  }

  h3 {
    margin: 18px 0 10px;
    color: #0f172a;
    font-size: 1.08rem;
    line-height: 1.35;
  }

  p {
    margin: 0;
    color: #64748b;
    line-height: 1.68;
  }
`;

const IconBox = styled.div`
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: #eff6ff;
  color: #2563eb;
`;

const SplitSection = styled(Section)`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(420px, 1.1fr);
  align-items: center;
  gap: clamp(32px, 6vw, 78px);

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const VisualStack = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.78fr;
  gap: 16px;
  align-items: end;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ImagePanel = styled.div`
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: white;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.1);

  img {
    display: block;
    width: 100%;
    height: ${({ tall }) => (tall ? '420px' : '260px')};
    object-fit: cover;
  }

  @media (max-width: 560px) {
    img {
      height: 240px;
    }
  }
`;

const Checklist = styled.ul`
  display: grid;
  gap: 14px;
  padding: 0;
  margin: 28px 0 0;
  list-style: none;
`;

const ChecklistItem = styled.li`
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: start;
  gap: 12px;
  color: #334155;
  line-height: 1.7;

  svg {
    margin-top: 3px;
    color: #0ea5e9;
    font-size: 1.1rem;
  }
`;

const MeterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const MeterTypeCard = styled.article`
  overflow: hidden;
  border: 1px solid #dbeafe;
  border-radius: 22px;
  background: white;
  box-shadow: 0 20px 55px rgba(15, 23, 42, 0.065);

  img {
    display: block;
    width: 100%;
    height: 300px;
    object-fit: cover;
    background: #eff6ff;
  }
`;

const MeterContent = styled.div`
  padding: 26px;

  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    color: #0f172a;
    font-size: 1.28rem;
  }

  p {
    margin: 14px 0 0;
    color: #64748b;
    line-height: 1.7;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const Tag = styled.span`
  padding: 8px 11px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 0.84rem;
  font-weight: 700;
`;

const HighlightBand = styled.section`
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #e0f2fe 100%),
    #f8fafc;
`;

const UseCaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const UseCaseCard = styled.article`
  padding: 22px;
  border: 1px solid rgba(14, 165, 233, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.82);

  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 12px;
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: #64748b;
    line-height: 1.65;
  }

  svg {
    color: #2563eb;
  }
`;

const WorkSection = styled(Section)`
  padding-top: 72px;
`;

const WorkGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: 24px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const WorkImage = styled.div`
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid #e2e8f0;

  img {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 360px;
    object-fit: cover;
  }
`;

const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const CompanyCard = styled.div`
  min-height: 150px;
  display: grid;
  place-items: center;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: white;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.05);

  img {
    max-width: 100%;
    max-height: 100px;
    object-fit: contain;
  }

  strong {
    color: #0f172a;
    text-align: center;
  }
`;

const FinalCta = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto 80px;
  padding: clamp(34px, 6vw, 58px);
  border-radius: 28px;
  background:
    radial-gradient(circle at 90% 20%, rgba(125, 211, 252, 0.22), transparent 32%),
    linear-gradient(135deg, #071428, #0f3a5f);
  color: white;
  text-align: center;

  h2 {
    max-width: 760px;
    margin: 0 auto;
    font-size: clamp(2rem, 4vw, 3.4rem);
    line-height: 1.08;
    letter-spacing: 0;
  }

  p {
    max-width: 700px;
    margin: 18px auto 28px;
    color: #dbeafe;
    line-height: 1.8;
  }
`;

const CtaActions = styled(HeroActions)`
  justify-content: center;
  margin-top: 0;
`;

const sectionVariants = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.52, ease: 'easeOut' } },
};

function AnimatedSection({ children, ...props }) {
  return (
    <Section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      {...props}
    >
      {children}
    </Section>
  );
}

const features = [
  {
    icon: <Sensors />,
    title: 'NB-IoT smart metering',
    text: 'Ultrasonic meters collect reliable consumption data without manual reading rounds, helping teams see usage as it happens.',
  },
  {
    icon: <Assessment />,
    title: 'Analytics and reporting',
    text: 'Dashboards turn meter readings into trends, comparisons, anomaly views, and exportable reports for operations and billing teams.',
  },
  {
    icon: <NotificationsActive />,
    title: 'Real-time alerts',
    text: 'Leak, burst, tamper, low balance, and abnormal usage alerts shorten response time and reduce avoidable losses.',
  },
  {
    icon: <Payments />,
    title: 'Prepaid water workflows',
    text: 'Support transparent water credit purchases, balance visibility, and faster reconnection for prepaid deployments.',
  },
  {
    icon: <CloudSync />,
    title: 'Cloud data access',
    text: 'Meter data is available through web and mobile dashboards so managers, utilities, and customers stay aligned.',
  },
  {
    icon: <DeviceHub />,
    title: 'Integration-ready APIs',
    text: 'Connect AquaTools with billing, customer care, finance, and field operations systems through structured integrations.',
  },
];

const meterTypes = [
  {
    icon: <HomeOutlined />,
    image: dom1,
    title: 'Domestic smart meters',
    text: 'Compact meters for homes, apartments, estates, and small businesses that need accurate billing, remote monitoring, and valve control.',
    tags: ['DN15 to DN25', 'Valve control', 'Residential billing', 'Leak alerts'],
  },
  {
    icon: <Business />,
    image: bulk3,
    title: 'Bulk smart meters',
    text: 'High-capacity metering for utilities, commercial buildings, industries, and distribution networks that need durable remote reads.',
    tags: ['DN40 to DN300+', 'Bulk flow data', 'Utility networks', 'NRW visibility'],
  },
];

const useCases = [
  {
    icon: <Map />,
    title: 'Utilities',
    text: 'Improve network visibility, meter reading accuracy, and response coordination across service zones.',
  },
  {
    icon: <Business />,
    title: 'Commercial sites',
    text: 'Track tenant usage, detect waste, and simplify allocation for estates, malls, hotels, and offices.',
  },
  {
    icon: <Shield />,
    title: 'Loss reduction',
    text: 'Flag unusual consumption, suspected leaks, tamper events, and potential non-revenue water sources.',
  },
  {
    icon: <Speed />,
    title: 'Field operations',
    text: 'Give teams clearer context before dispatch through meter status, historical reads, and alerts.',
  },
];

const partners = [
  { image: wra, name: 'Water Resources Authority' },
  { image: gatanga, name: 'Gatanga Water' },
  { image: ruiru, name: 'Ruiru Juja Water and Sewarage' },
  { image: bamburi, name: 'Bamburi Cement' },
  { image: aquamist, name: 'Aquamist' },
  {image: farmers, name:'Farmers Choice'}
];

const Home = () => {
  return (
    <Main>
      <Helmet>
        <title>AquaTools | Smart Water Metering Solutions</title>
        <meta
          name="description"
          content="AquaTools provides real-time water meter analytics, smart monitoring, and historical data insights to help you manage water usage efficiently."
        />
        <meta
          name="keywords"
          content="water meter, smart water monitoring, water analytics, water usage tracking, IoT water metering, utility analytics, AquaTools Kenya"
        />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://aquatools.io/" />
        <link rel="alternate" href="https://aquatools.io/about" title="About AquaTools" />
        <link rel="alternate" href="https://aquatools.io/contactus" title="Contact Us" />
        <link rel="alternate" href="https://aquatools.io/login" title="Login" />
        <link rel="alternate" href="https://aquatools.io/products" title="Products" />
        <meta property="og:title" content="AquaTools | Smart Water Metering Solutions" />
        <meta property="og:description" content="Smart IoT water meters with real-time usage tracking and analytics." />
        <meta property="og:url" content="https://aquatools.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://aquatools.io/logo.png" />
        <meta property="og:site_name" content="AquaTools" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:updated_time" content="2025-04-19T12:00:00Z" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AquaTools | Smart Water Metering Solutions" />
        <meta name="twitter:description" content="Monitor, analyze, and optimize your water usage with AquaTools." />
        <meta name="twitter:image" content="https://aquatools.io/logo.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'AquaTools',
              url: 'https://aquatools.io',
              logo: 'https://aquatools.io/logo.png',
              sameAs: [
                'https://www.linkedin.com/company/aquatools',
                'https://www.facebook.com/aquatools',
                'https://www.tiktok.com/@aquatools',
              ],
            }),
          }}
        />
      </Helmet>

      <Navbar />

      <Container>
        <Hero>
          <HeroInner>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <Eyebrow>
                <WaterDrop fontSize="small" />
                Smart water intelligence
              </Eyebrow>
              <Heading>Modern water metering for connected utilities and communities.</Heading>
              <HeroText>
                AquaTools combines NB-IoT ultrasonic meters, prepaid workflows, analytics, alerts,
                and cloud dashboards.
              </HeroText>

              <HeroActions>
                <StyledLink to="/contactus">
                  <Button>
                    Talk to us
                    <ArrowForward fontSize="small" />
                  </Button>
                </StyledLink>
                <StyledLink to="/products">
                  <SecondaryButton>View meters</SecondaryButton>
                </StyledLink>
              </HeroActions>

              <Stats>
                <Stat>
                  <strong>24/7</strong>
                  <span>Remote consumption and meter status visibility</span>
                </Stat>
                <Stat>
                  <strong>DN15+</strong>
                  <span>Domestic and bulk meter sizes for varied deployments</span>
                </Stat>
                <Stat>
                  <strong>API</strong>
                  <span>Integration support for billing and operations systems</span>
                </Stat>
              </Stats>
            </MotionDiv>

            <HeroVisual>
              <DeviceFrame>
                <img src={dashboard} alt="AquaTools monitoring dashboard" />
            
              </DeviceFrame>
              
            </HeroVisual>
          </HeroInner>
        </Hero>

        <AnimatedSection>
          <SectionHeader centered>
            <Kicker>
              <DeviceHub fontSize="small" />
              Platform
            </Kicker>
            <Title>Everything needed to measure, monitor, and act on water data.</Title>
            <Text>
              AquaTools is built for the full metering workflow: device deployment, data collection,
              cloud visibility, customer transparency, and day-to-day operational decisions.
            </Text>
          </SectionHeader>

          <FeatureGrid>
            {features.map(({ icon, title, text }) => (
              <FeatureCard key={title}>
                <IconBox>{icon}</IconBox>
                <h3>{title}</h3>
                <p>{text}</p>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </AnimatedSection>

        <SplitSection
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div>
            <Kicker>
              <Analytics fontSize="small" />
              Analytics
            </Kicker>
            <Title>Turn meter readings into faster, clearer decisions.</Title>
            <Text>
              The dashboard helps teams filter by time, account, location, or meter class, then
              review trends, export reports, and isolate abnormal usage before it becomes costly.
            </Text>

            <Checklist>
              <ChecklistItem>
                <CheckCircle />
                <span>Historical records for usage audits, customer support, billing checks, and demand planning.</span>
              </ChecklistItem>
              <ChecklistItem>
                <CheckCircle />
                <span>Anomaly views that help identify leaks, bursts, zero-flow meters, and unusual consumption patterns.</span>
              </ChecklistItem>
              <ChecklistItem>
                <CheckCircle />
                <span>Responsive dashboards for managers, technical teams, and customers who need quick clarity.</span>
              </ChecklistItem>
            </Checklist>
          </div>

          <VisualStack>
            <ImagePanel tall>
              <img src={data} alt="Water usage analytics dashboard" />
            </ImagePanel>
            <div>
              <ImagePanel>
                <img src={dash1} alt="AquaTools dashboard summary" />
              </ImagePanel>
              <ImagePanel style={{ marginTop: 16 }}>
                <img src={Table} alt="AquaTools data table" />
              </ImagePanel>
            </div>
          </VisualStack>
        </SplitSection>

        <HighlightBand>
          <AnimatedSection>
            <SectionHeader>
              <Kicker>
                <WaterDrop fontSize="small" />
                Metering
              </Kicker>
              <Title>Smart meters for homes, estates, utilities, and bulk water networks.</Title>
              <Text>
                Whether the goal is transparent household billing or large-scale distribution
                monitoring, AquaTools provides meter options that pair field hardware with digital control.
              </Text>
            </SectionHeader>

            <MeterGrid>
              {meterTypes.map(({ icon, image, title, text, tags }) => (
                <MeterTypeCard key={title}>
                  <img src={image} alt={title} />
                  <MeterContent>
                    <h3>
                      {icon}
                      {title}
                    </h3>
                    <p>{text}</p>
                    <TagList>
                      {tags.map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </TagList>
                  </MeterContent>
                </MeterTypeCard>
              ))}
            </MeterGrid>
          </AnimatedSection>
        </HighlightBand>

        <SplitSection
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <VisualStack>
            <ImagePanel tall>
              <img src={conserve} alt="Water conservation with smart metering" />
            </ImagePanel>
            <div>
              <ImagePanel>
                <img src={smartmetering} alt="Smart metering installation" />
              </ImagePanel>
              <ImagePanel style={{ marginTop: 16 }}>
                <img src={Installation1} alt="Prepaid metering installation" />
              </ImagePanel>
            </div>
          </VisualStack>

          <div>
            <Kicker>
              <Shield fontSize="small" />
              Conservation
            </Kicker>
            <Title>Reduce waste with visibility at the point of consumption.</Title>
            <Text>
              Real-time metering helps customers and utilities spot leaks, track usage behavior,
              and respond faster to supply faults. That visibility supports better billing, lower
              non-revenue water, and more responsible consumption.
            </Text>

            <Checklist>
              <ChecklistItem>
                <CheckCircle />
                <span>Leak and burst alerts help prevent invisible losses from becoming long-running expenses.</span>
              </ChecklistItem>
              <ChecklistItem>
                <CheckCircle />
                <span>Consumption feedback gives households and organizations a practical way to manage demand.</span>
              </ChecklistItem>
              <ChecklistItem>
                <CheckCircle />
                <span>Remote reads reduce manual visits while improving accuracy and operational planning.</span>
              </ChecklistItem>
            </Checklist>
          </div>
        </SplitSection>

        <AnimatedSection>
          <SectionHeader centered>
            <Kicker>
              <Map fontSize="small" />
              Use cases
            </Kicker>
            <Title>Built for the teams responsible for reliable water service.</Title>
            <Text>
              AquaTools gives technical, finance, and customer support teams one shared view of
              metering data, field events, and usage history.
            </Text>
          </SectionHeader>

          <UseCaseGrid>
            {useCases.map(({ icon, title, text }) => (
              <UseCaseCard key={title}>
                <h3>
                  {icon}
                  {title}
                </h3>
                <p>{text}</p>
              </UseCaseCard>
            ))}
          </UseCaseGrid>
        </AnimatedSection>

        <WorkSection
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <SectionHeader>
            <Kicker>
              <Business fontSize="small" />
              Works and partners
            </Kicker>
            <Title>Field-ready solutions backed by practical deployment experience.</Title>
            <Text>
              From installations to monitoring dashboards, AquaTools supports smart metering projects
              for utilities, institutions, commercial sites, and engineering partners.
            </Text>
          </SectionHeader>

          <WorkGrid>
            <WorkImage>
              <img src={Installation3} alt="AquaTools meter installation work" />
            </WorkImage>
            <CompanyGrid>
              {partners.map(({ image, name }) => (
                <CompanyCard key={name}>
                  {image ? <img src={image} alt={name} /> : <strong>{name}</strong>}
                </CompanyCard>
              ))}
           
            </CompanyGrid>
          </WorkGrid>
        </WorkSection>

        <FinalCta>
          <h2>Ready to modernize water metering with real operational visibility?</h2>
          <p>
            Tell us about your site, estate, utility, or bulk network. We will help match the right
            meter category, dashboard workflow, and deployment path to your needs.
          </p>
          <CtaActions>
            <StyledLink to="/contactus">
              <Button>
                Start a conversation
                <ArrowForward fontSize="small" />
              </Button>
            </StyledLink>
            <StyledLink to="/about">
              <SecondaryButton>Learn more</SecondaryButton>
            </StyledLink>
          </CtaActions>
        </FinalCta>
      </Container>

      <Footer />
    </Main>
  );
};

export default Home;
